/**
 * PREMIUM VIDEO HERO — behavior
 * Vanilla JavaScript. No dependencies. No jQuery.
 * Handles: scroll-triggered entrance animations, video play/pause + mute
 * controls, countdown timers, scroll effects (parallax/zoom/blur), and
 * floating particle generation. Fully respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  var PREFERS_REDUCED_MOTION = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  /** Initialize every hero instance on the page (theme editor may add more later). */
  function initAll(root) {
    var sections = (root || document).querySelectorAll('[data-pvh-section]');
    sections.forEach(initSection);
  }

  function initSection(section) {
    if (!section || section.dataset.pvhInitialized === 'true') return;
    section.dataset.pvhInitialized = 'true';

    initEntranceAnimations(section);
    initVideoControls(section);
    initCountdowns(section);
    initScrollEffect(section);
    initParticles(section);
    initScrollIndicator(section);
  }

  /* --------------------------------------------------------------------
     ENTRANCE ANIMATIONS
     -------------------------------------------------------------------- */
  function initEntranceAnimations(section) {
    var items = section.querySelectorAll('.pvh-anim');
    if (!items.length) return;

    if (PREFERS_REDUCED_MOTION || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('pvh-anim--visible');
      });
      return;
    }

    var playOnce = section.dataset.animationOnce !== 'false';
    var repeat = section.dataset.animationRepeat === 'true';

    items.forEach(function (el) {
      var delay = el.getAttribute('data-animation-delay') || 0;
      var duration = el.getAttribute('data-animation-duration') || 700;
      el.style.transitionDelay = delay + 'ms';
      el.style.transitionDuration = duration + 'ms';
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('pvh-anim--visible');
            if (playOnce && !repeat) {
              observer.unobserve(entry.target);
            }
          } else if (repeat) {
            entry.target.classList.remove('pvh-anim--visible');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -5% 0px' }
    );

    items.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* --------------------------------------------------------------------
     VIDEO CONTROLS (play/pause, mute/unmute)
     -------------------------------------------------------------------- */
  function initVideoControls(section) {
    var video = section.querySelector('.pvh-media__video--desktop video, .pvh-media__video--mobile video');
    if (!video) return;

    var playPauseBtn = section.querySelector('[data-pvh-playpause]');
    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', function () {
        var playing = !video.paused && !video.ended;
        if (playing) {
          video.pause();
        } else {
          video.play().catch(function () {
            /* Autoplay/play restrictions: fail silently, control still toggles state */
          });
        }
        togglePlayPauseUI(playPauseBtn, !playing);
      });

      video.addEventListener('pause', function () { togglePlayPauseUI(playPauseBtn, false); });
      video.addEventListener('play', function () { togglePlayPauseUI(playPauseBtn, true); });
    }

    var muteBtn = section.querySelector('[data-pvh-mute]');
    if (muteBtn) {
      muteBtn.addEventListener('click', function () {
        video.muted = !video.muted;
        toggleMuteUI(muteBtn, video.muted);
      });
    }
  }

  function togglePlayPauseUI(btn, isPlaying) {
    var playIcon = btn.querySelector('.pvh-icon--play');
    var pauseIcon = btn.querySelector('.pvh-icon--pause');
    if (playIcon) playIcon.hidden = isPlaying;
    if (pauseIcon) pauseIcon.hidden = !isPlaying;
    btn.setAttribute('aria-pressed', String(!isPlaying));
    btn.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
  }

  function toggleMuteUI(btn, isMuted) {
    var mutedIcon = btn.querySelector('.pvh-icon--muted');
    var unmutedIcon = btn.querySelector('.pvh-icon--unmuted');
    if (mutedIcon) mutedIcon.hidden = !isMuted;
    if (unmutedIcon) unmutedIcon.hidden = isMuted;
    btn.setAttribute('aria-pressed', String(isMuted));
    btn.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
  }

  /* --------------------------------------------------------------------
     COUNTDOWN TIMER
     -------------------------------------------------------------------- */
  function initCountdowns(section) {
    var timers = section.querySelectorAll('[data-pvh-countdown]');
    timers.forEach(function (timer) {
      var endDateStr = timer.getAttribute('data-end-date');
      if (!endDateStr) return;

      var endDate = new Date(endDateStr);
      if (isNaN(endDate.getTime())) return;

      var daysEl = timer.querySelector('[data-pvh-days]');
      var hoursEl = timer.querySelector('[data-pvh-hours]');
      var minutesEl = timer.querySelector('[data-pvh-minutes]');
      var secondsEl = timer.querySelector('[data-pvh-seconds]');

      var intervalId = setInterval(update, 1000);
      update();

      function update() {
        var now = new Date().getTime();
        var distance = endDate.getTime() - now;

        if (distance <= 0) {
          clearInterval(intervalId);
          if (daysEl) daysEl.textContent = '00';
          if (hoursEl) hoursEl.textContent = '00';
          if (minutesEl) minutesEl.textContent = '00';
          if (secondsEl) secondsEl.textContent = '00';
          return;
        }

        var days = Math.floor(distance / (1000 * 60 * 60 * 24));
        var hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        var seconds = Math.floor((distance % (1000 * 60)) / 1000);

        if (daysEl) daysEl.textContent = pad(days);
        if (hoursEl) hoursEl.textContent = pad(hours);
        if (minutesEl) minutesEl.textContent = pad(minutes);
        if (secondsEl) secondsEl.textContent = pad(seconds);
      }

      function pad(n) {
        return String(n).length < 2 ? '0' + n : String(n);
      }
    });
  }

  /* --------------------------------------------------------------------
     SCROLL EFFECTS (parallax / zoom / blur)
     Only runs while the section is on-screen, throttled via rAF.
     -------------------------------------------------------------------- */
  function initScrollEffect(section) {
    var effect = section.dataset.scrollEffect;
    if (!effect || effect === 'none' || PREFERS_REDUCED_MOTION) return;

    var media = section.querySelector('.pvh-media');
    if (!media) return;

    var intensity = parseFloat(section.dataset.scrollEffectIntensity || '30') / 100;
    var ticking = false;
    var isVisible = false;

    if ('IntersectionObserver' in window) {
      var visibilityObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            isVisible = entry.isIntersecting;
          });
        },
        { threshold: 0 }
      );
      visibilityObserver.observe(section);
    } else {
      isVisible = true;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!isVisible || ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          applyEffect();
          ticking = false;
        });
      },
      { passive: true }
    );

    function applyEffect() {
      var rect = section.getBoundingClientRect();
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      var progress = 1 - Math.min(Math.max((rect.top + rect.height / 2) / (viewportHeight + rect.height), 0), 1);

      if (effect === 'parallax') {
        var translate = (progress - 0.5) * 120 * intensity;
        media.style.transform = 'translate3d(0,' + translate.toFixed(2) + 'px,0)';
      } else if (effect === 'zoom') {
        var scale = 1 + progress * 0.25 * intensity;
        media.style.transform = 'scale(' + scale.toFixed(3) + ')';
      } else if (effect === 'blur') {
        var blurAmount = progress * 12 * intensity;
        media.style.filter = 'blur(' + blurAmount.toFixed(2) + 'px)';
      }
    }
  }

  /* --------------------------------------------------------------------
     FLOATING PARTICLES
     -------------------------------------------------------------------- */
  function initParticles(section) {
    if (section.dataset.showParticles !== 'true' || PREFERS_REDUCED_MOTION) return;

    var container = section.querySelector('[data-pvh-particles]');
    if (!container) return;

    var count = 24;
    var fragment = document.createDocumentFragment();

    for (var i = 0; i < count; i++) {
      var particle = document.createElement('span');
      particle.className = 'pvh-particle';
      particle.style.left = Math.random() * 100 + '%';
      particle.style.animationDuration = 8 + Math.random() * 10 + 's';
      particle.style.animationDelay = Math.random() * 10 + 's';
      particle.style.opacity = String(0.3 + Math.random() * 0.5);
      fragment.appendChild(particle);
    }

    container.appendChild(fragment);
  }

  /* --------------------------------------------------------------------
     SCROLL DOWN INDICATOR
     -------------------------------------------------------------------- */
  function initScrollIndicator(section) {
    var indicator = section.querySelector('[data-pvh-scroll-indicator]');
    if (!indicator) return;

    indicator.addEventListener('click', function () {
      var next = section.nextElementSibling;
      var target = next || section;
      var top = target.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: next ? top : top + section.offsetHeight,
        behavior: PREFERS_REDUCED_MOTION ? 'auto' : 'smooth'
      });
    });
  }

  /* --------------------------------------------------------------------
     INIT + Shopify Theme Editor support
     -------------------------------------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-initialize newly added/re-rendered sections inside the Theme Editor.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
